import * as React from "react";
import { useState, useEffect } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { getSession } from "../session";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Alert from "@mui/material/Alert";
import { alpha } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EventIcon from "@mui/icons-material/Event";
import FlightIcon from "@mui/icons-material/Flight";
import LockIcon from "@mui/icons-material/Lock";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import CircularProgress from "@mui/material/CircularProgress";
import { Container, AppBar, Toolbar, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSubscriptionContext } from "./SubscriptionContext";
import { TIER_CONFIG } from "./membershipTiers";

// Estados de suscripción
const SUBSCRIPTION_STATUS = {
  NONE: "NONE",
  ACTIVE: "ACTIVE",
  CANCELLED: "CANCELLED",
  EXPIRED: "EXPIRED",
};

// Configuración de planes
const plans = {
  mensual: [
    {
      name: "Básico",
      price: 5.99,
      plan_id: "P-2RM407508J8295447M5BTBXY",
      features: [
        "12 propuestas por mes",
        "Soporte básico",
        "Insignia Plata en tu perfil",
        "Acceso a proyectos de hasta USD$300",
        "Perfil visible en la búsqueda de freelancers",
      ],
    },
    {
      name: "Profesional",
      price: 12.99,
      plan_id: "P-4M882979D7330063EM5BTBII",
      features: [
        "20 propuestas por mes",
        "Soporte prioritario",
        "Insignia Oro en tu perfil",
        "Acceso a proyectos de hasta USD$1500",
        "Aparecés antes que los planes Básico en los resultados de búsqueda",
      ],
    },
    {
      name: "Premium",
      price: 15.99,
      plan_id: "P-0LS22755GY946183VM5BTAYQ",
      features: [
        "Propuestas ilimitadas",
        "Soporte 24/7",
        "Insignia Platino en tu perfil",
        "Acceso a todos los proyectos, sin límite de presupuesto",
        "Prioridad máxima en los resultados de búsqueda",
      ],
    },
  ],
  anual: [
    {
      name: "Básico",
      price: 149.99,
      plan_id: "P-50W39415KE575415PM5BS63I",
      features: [
        "12 propuestas por mes",
        "Soporte básico",
        "Insignia Plata en tu perfil",
        "Acceso a proyectos de hasta USD$300",
        "Perfil visible en la búsqueda de freelancers",
      ],
    },
    {
      name: "Profesional",
      price: 319.99 ,
      plan_id: "P-3ES61533YH502224FM5BS7RY",
      features: [
        "20 propuestas por mes",
        "Soporte prioritario",
        "Insignia Oro en tu perfil",
        "Acceso a proyectos de hasta USD$1500",
        "Aparecés antes que los planes Básico en los resultados de búsqueda",
      ],
    },
    {
      name: "Premium",
      price: 1539.99,
      plan_id: "P-5FJ12565US198171YM5BTADQ",
      features: [
        "Propuestas ilimitadas",
        "Soporte 24/7",
        "Insignia Platino en tu perfil",
        "Acceso a todos los proyectos, sin límite de presupuesto",
        "Prioridad máxima en los resultados de búsqueda",
      ],
    },
  ],
};

// Componente para iconos de categoría
const CategoryIcon = ({ category }) => {
  switch (category) {
    case "ofertas":
      return <LocalOfferIcon sx={{ color: "success.main" }} />;
    case "eventos":
      return <EventIcon sx={{ color: "primary.main" }} />;
    case "viajes":
      return <FlightIcon sx={{ color: "info.main" }} />;
    case "autos":
      return <DirectionsCarIcon sx={{ color: "warning.main" }} />;
    default:
      return <CheckCircleOutlineIcon color="success" />;
  }
};
const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const session = await getSession();
        setUser(session);
      } catch (error) {
        console.error("Error getting session:", error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  return { user, loading };
};



// Componente de selección de plan
const PlanSelection = ({
  selectedPlan,
  setSelectedPlan,
  billingInterval,
  setBillingInterval,
}) => {
  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
        <ToggleButtonGroup
          value={billingInterval}
          exclusive
          onChange={(_, newInterval) =>
            newInterval && setBillingInterval(newInterval)
          }
          aria-label="intervalo de facturación"
        >
          <ToggleButton value="mensual">Mensual</ToggleButton>
          <ToggleButton value="anual">
            Anual
            <Typography
              component="span"
              sx={{
                ml: 1,
                px: 1,
                py: 0.5,
                bgcolor: "success.light",
                color: "success.contrastText",
                borderRadius: "pill",
                fontSize: "0.75rem",
              }}
            >
              Ahorra 20%
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {plans[billingInterval].map((plan) => (
          <Grid item xs={12} md={4} key={plan.name}>
            <Card
              onClick={() => setSelectedPlan(plan)}
              sx={{
                cursor: "pointer",
                height: "100%",
                transition: "all 0.2s",
                border: selectedPlan?.name === plan.name ? 2 : 1,
                borderColor:
                  selectedPlan?.name === plan.name ? "primary.main" : "divider",
                boxShadow:
                  selectedPlan?.name === plan.name
                    ? (theme) => `0 0 16px ${alpha(theme.palette.primary.main, 0.35)}`
                    : "none",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: (theme) =>
                    `0 0 16px ${alpha(theme.palette.primary.main, 0.35)}`,
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {TIER_CONFIG[plan.name]?.badge && (
                    (() => {
                      const { Icon, color } = TIER_CONFIG[plan.name].badge;
                      return <Icon sx={{ color }} />;
                    })()
                  )}
                  <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
                    Plan {plan.name}
                  </Typography>
                </Box>
                {TIER_CONFIG[plan.name]?.badge && (
                  <Typography variant="caption" color="text.secondary">
                    Insignia {TIER_CONFIG[plan.name].badge.label}
                  </Typography>
                )}
                <Typography variant="h4" component="div" gutterBottom>
                  ${plan.price}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    /{billingInterval === "mensual" ? "mes" : "año"}
                  </Typography>
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {plan.features.map((feature) => {
                    const category = feature.includes("ofertas")
                      ? "ofertas"
                      : feature.includes("eventos")
                      ? "eventos"
                      : feature.includes("viajes")
                      ? "viajes"
                      : feature.includes("autos")
                      ? "autos"
                      : "general";

                    return (
                      <Box
                        key={feature}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CategoryIcon category={category} />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

// Componente de método de pago
const PaymentMethod = ({
  selectedPlan,
  billingInterval,
  onPaymentComplete,
}) => {
  // Agregamos billingInterval como prop
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paypalKey, setPaypalKey] = useState(0);
  const { user } = useAuth(); // Obtenemos user del hook useAuth

  useEffect(() => {
    setPaypalKey((prev) => prev + 1);
  }, [selectedPlan]);

  const paypalOptions = {
    "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
    currency: "USD",
    intent: "subscription",
    vault: true,
    locale: "es_AR",
    "data-sdk-integration-source": "button-factory",
  };

  const handleCreateSubscription = async (data, actions) => {
    try {
      setLoading(true);

      if (!selectedPlan?.plan_id) {
        throw new Error("Plan ID no disponible");
      }

      if (!user?.uid) {
        throw new Error("Usuario no autenticado");
      }

      return actions.subscription.create({
        plan_id: selectedPlan.plan_id,
        // Enlaza la suscripción de PayPal con el usuario de Firebase.
        // El webhook del backend lee este custom_id para activar la cuenta.
        custom_id: user.uid,
        application_context: {
          shipping_preference: "NO_SHIPPING",
          user_action: "SUBSCRIBE_NOW",
          brand_name: "Notiline",
          locale: "es-ES",
        },
      });
    } catch (err) {
      console.error("Error al crear suscripción:", err);
      setError(`Error al crear la suscripción: ${err.message}`);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (data, actions) => {
    if (!user) {
      setError("Usuario no autenticado");
      return;
    }

    try {
      setLoading(true);
      const subscriptionDetails = await actions.subscription.get();

      // No escribimos el estado desde el cliente: la activación la confirma
      // el webhook de PayPal en el backend (users/{uid}.subscription).
      // SubscriptionContext escucha ese doc con onSnapshot, así que la UI
      // pasa a "ACTIVE" sola cuando llega el evento verificado por PayPal.
      onPaymentComplete(subscriptionDetails);
    } catch (err) {
      setError("Error al procesar el pago. Por favor, contacte a soporte.");
      console.error("Error detallado:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mb: 10 }}>
      <PayPalScriptProvider options={paypalOptions}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ mb: 3, width: "100%" }}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ width: "100%", mb: 3 }}>
            <Box
              sx={{
                p: 3,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "background.paper",
                position: "relative",
                display: "flex",
                justifyContent: "center",
                "& .paypal-buttons": {
                  maxWidth: "500px",
                  minWidth: "250px",
                  width: "100% !important",
                },
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: (theme) =>
                      alpha(theme.palette.background.paper, 0.8),
                    zIndex: 1,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}

              {/* Añadimos key al PayPalButtons para forzar su reinicio */}
              <PayPalButtons
                key={paypalKey}
                style={{
                  shape: "rect",
                  color: "blue",
                  layout: "vertical",
                  label: "subscribe",
                }}
                createSubscription={handleCreateSubscription}
                onApprove={handleApprove}
                onError={(err) => setError("Ocurrió un error con PayPal.")}
                onCancel={() => setLoading(false)}
              />
            </Box>

            <Alert severity="info" sx={{ mt: 3, width: "100%" }}>
              No necesitas tener una cuenta de PayPal - puedes pagar con tarjeta
            </Alert>
          </Box>

          <Alert severity="success" icon={<LockIcon />} sx={{ width: "100%" }}>
            Pago seguro procesado por PayPal
          </Alert>
        </Box>
      </PayPalScriptProvider>
    </Container>
  );
};

const SubscriptionStatus = ({ subscriptionInfo, onCancelSubscription }) => {
  const [currentStatus, setCurrentStatus] = useState(subscriptionInfo.status);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { user } = useAuth();

  const handleCancelSubscription = async () => {
    if (!user?.uid || !subscriptionInfo.subscriptionId) return;
    setIsUpdating(true);
    setError(null);

    try {
      // La cancelación efectiva se hace en PayPal; el webhook
      // (BILLING.SUBSCRIPTION.CANCELLED) actualiza luego el estado en Firebase.
      // No escribimos "CANCELLED" desde el cliente.
      setCurrentStatus("CANCELLED"); // feedback optimista en la UI
      onCancelSubscription && onCancelSubscription();

      // Redireccionar a PayPal para cancelar la suscripción.
      window.open("https://www.paypal.com/myaccount/autopay", "_blank");

      setShowConfirmation(false);
    } catch (error) {
      console.error("Error canceling subscription:", error);
      setError(
        "Error al cancelar la suscripción. Por favor intente nuevamente."
      );
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper functions remain the same...
  const getStatusDisplay = (status) => {
    const statusMap = {
      ACTIVE: { text: "Activa", color: "success.main" },
      EXPIRED: { text: "Expirada", color: "error.main" },
      CANCELLED: { text: "Cancelada", color: "error.main" },
      SUSPENDED: { text: "Suspendida", color: "warning.main" },
    };
    return statusMap[status] || { text: status, color: "text.primary" };
  };

  const getDaysRemaining = () => {
    if (!subscriptionInfo.nextBillingDate) return null;
    const nextBilling = new Date(subscriptionInfo.nextBillingDate);
    const now = new Date();
    const diffTime = nextBilling - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const statusDisplay = getStatusDisplay(currentStatus);
  const daysRemaining = getDaysRemaining();

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Card>
        <CardContent>
          <Stack spacing={3}>
            <Box sx={{ textAlign: "center" }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 64, color: statusDisplay.color, mb: 2 }}
              />
              <Typography variant="h5" gutterBottom>
                Suscripción {statusDisplay.text}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Detalles de la suscripción:
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Plan:</Typography>
                  <Typography>{subscriptionInfo.planName}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Precio:</Typography>
                  <Typography>
                    ${subscriptionInfo.price}/{subscriptionInfo.interval}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Estado:</Typography>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography color={statusDisplay.color}>
                      {statusDisplay.text}
                    </Typography>
                    {isUpdating && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>ID de Suscripción:</Typography>
                  <Typography>{subscriptionInfo.subscriptionId}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography>Próximo cobro:</Typography>
                  <Typography>
                    {subscriptionInfo.nextBillingDate ? (
                      <>
                        {new Date(
                          subscriptionInfo.nextBillingDate
                        ).toLocaleDateString()}
                        {daysRemaining > 0 && (
                          <Typography
                            component="span"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            ({daysRemaining} días)
                          </Typography>
                        )}
                      </>
                    ) : (
                      "No disponible"
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {currentStatus === "ACTIVE" && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {!showConfirmation ? (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowConfirmation(true)}
                    startIcon={<CancelIcon />}
                    disabled={isUpdating}
                  >
                    Cancelar Suscripción
                  </Button>
                ) : (
                  <Box sx={{ width: "100%", textAlign: "center" }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      Para completar la cancelación necesitarás:
                      <ol style={{ marginTop: "10px", textAlign: "left" }}>
                        1. Confirmar aquí para actualizar tu estado 2. Cancelar
                        en PayPal (se abrirá en una nueva ventana)
                      </ol>
                    </Alert>
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Button
                        variant="outlined"
                        onClick={() => setShowConfirmation(false)}
                      >
                        No, mantener suscripción
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={handleCancelSubscription}
                        disabled={isUpdating}
                        startIcon={
                          isUpdating ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CancelIcon />
                          )
                        }
                      >
                        Sí, cancelar suscripción
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            )}

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

// Componente de éxito
const SubscriptionSuccess = ({ subscriptionInfo, onContinue }) => {
  return (
    <Stack
      spacing={3}
      alignItems="center"
      sx={{ py: 4, maxWidth: 600, mx: "auto" }}
    >
      <CheckCircleOutlineIcon sx={{ fontSize: 64, color: "success.main" }} />
      <Typography variant="h5">¡Suscripción Exitosa!</Typography>
      <Typography variant="body1" color="text.secondary" align="center">
        Gracias por suscribirte. Tu suscripción está activa y ya puedes comenzar
        a disfrutar de todos los beneficios.
      </Typography>
      <Box sx={{ width: "100%" }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Detalles de tu suscripción:
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>ID de Suscripción:</Typography>
                <Typography>{subscriptionInfo.subscriptionId}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>Plan:</Typography>
                <Typography>{subscriptionInfo.planName}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography>Estado:</Typography>
                <Typography color="success.main">
                  {subscriptionInfo.status}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Box>
      <Button
        variant="contained"
        size="large"
        onClick={onContinue}
        endIcon={<ArrowForwardIcon />}
      >
        Continuar a mi cuenta
      </Button>
    </Stack>
  );
};

const SubscriptionCheckout = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingInterval, setBillingInterval] = useState("mensual");
  const [subscriptionComplete, setSubscriptionComplete] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const navigate = useNavigate();

  // Get subscription context and auth
  const {
    subscription,
    loading: subscriptionLoading,
  } = useSubscriptionContext();
  const { user, loading: authLoading } = useAuth();

  // Show loading state while either auth or subscription is loading
  if (authLoading || subscriptionLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    navigate("/login");
    return null;
  }

  if (subscription?.status === SUBSCRIPTION_STATUS.ACTIVE) {
    return (
      <Container>
        <AppBar position="fixed">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate(-1)}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ flexGrow: 1, textAlign: "center", fontWeight: "bold" }}
            >
              Estado de Suscripción
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ pt: 8 }}>
          <SubscriptionStatus
            subscriptionInfo={subscription}
            onCancelSubscription={() => {
              // La baja se procesa en PayPal y la confirma el webhook
              // (BILLING.SUBSCRIPTION.CANCELLED). No escribimos el estado acá.
              navigate("/home");
            }}
          />
        </Box>
      </Container>
    );
  }

  if (subscriptionComplete) {
    return (
      <Container>
        <AppBar position="fixed">
          <Toolbar>
            <Typography
              variant="h6"
              sx={{ flexGrow: 1, textAlign: "center", fontWeight: "bold" }}
            >
              Suscripción Completada
            </Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ pt: 8 }}>
          <SubscriptionSuccess
            subscriptionInfo={subscriptionDetails}
            onContinue={() => navigate("/home")}
          />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
            <ChevronLeftRoundedIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, textAlign: "center", fontWeight: "bold" }}
          >
            Subscripción
          </Typography>
        </Toolbar>
      </AppBar>
      <Grid container spacing={3} sx={{ mt: 5, pt: 10 }}>
        <Grid item xs={12}>
          <PlanSelection
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            billingInterval={billingInterval}
            setBillingInterval={setBillingInterval}
          />
        </Grid>
        {selectedPlan && (
          <Grid item xs={12}>
            <PaymentMethod
              selectedPlan={selectedPlan}
              billingInterval={billingInterval}
              onPaymentComplete={(details) => {
                // El estado real (ACTIVE) lo escribe el webhook de PayPal.
                // Acá solo mostramos la pantalla de confirmación con los datos
                // del plan elegido; cuando el webhook actualice el doc, el
                // listener de SubscriptionContext reflejará "Activa".
                setSubscriptionDetails({
                  subscriptionId: details?.id || null,
                  planName: selectedPlan.name,
                  price: selectedPlan.price,
                  interval: billingInterval,
                  nextBillingDate:
                    details?.billing_info?.next_billing_time || null,
                });
                setSubscriptionComplete(true);
              }}
            />
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default SubscriptionCheckout;
